/**
 * Colour maths — PURE: hex parsing, WCAG contrast, and tints composited over white.
 *
 * One home for it (DESIGN_SYSTEM.md §3.1): the kiosk derives its merchant theme with it
 * (`kiosk/config/configModel`, which re-exports these), the design system builds and tests its
 * themes with it (`@rista/ui/library/theme`).
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Accepts #rgb and #rrggbb. Returns null for anything else — caller falls back. */
export function parseHex(hex: string): Rgb | null {
  const value = hex?.trim().replace(/^#/, "");
  if (!value) return null;

  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;

  if (full.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(full)) return null;

  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

export const toHex = (n: number) =>
  Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, "0");

/** `#rrggbb`, lowercase, from channels. */
export const rgbToHex = ({ r, g, b }: Rgb) => `#${toHex(r)}${toHex(g)}${toHex(b)}`;

/** WCAG relative luminance. Used to decide readable text on a coloured fill. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio, 1..21. */
export function contrastRatio(a: string, b: string): number {
  const rgbA = parseHex(a);
  const rgbB = parseHex(b);
  if (!rgbA || !rgbB) return 1;
  const [hi, lo] = [relativeLuminance(rgbA), relativeLuminance(rgbB)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Moves each channel `amount` (0..1) of the way to `target` (0 = black, 255 = white). */
export const mix = (rgb: Rgb, target: number, amount: number) =>
  rgbToHex({
    r: rgb.r + (target - rgb.r) * amount,
    g: rgb.g + (target - rgb.g) * amount,
    b: rgb.b + (target - rgb.b) * amount,
  });

/**
 * A colour at `opacity`, composited over WHITE, as an opaque hex.
 *
 * Takes the opacity the design specifies — `tint(rgb, 0.06)` is "6% primary" — rather
 * than the blend amount, because `mix(rgb, 255, 0.94)` for the same thing is one
 * inversion away from a silent off-by-a-lot.
 *
 * Opaque on purpose: `rgba(…, 0.06)` is only the designed colour when what is behind it
 * is white, and it drifts over `surface`, over a banner, and wherever two tints overlap
 * — differently under React Native and react-native-web. Blending once here gives one
 * canonical value that also compares as a string.
 */
export function tint(rgb: Rgb, opacity: number): string {
  return mix(rgb, 255, 1 - opacity);
}
